import {
  PickedCandidate,
  PickedCandidateWithNewestStatus,
} from "../../../TalentsStatusAPI";
import {
  CANDIDATE_STATUS_SORT_ORDER,
  CandidateStatus,
  TALENT_STATUS_SORT_ORDER,
} from "../../constants";

/**
 * Determines the most recent candidate status based on timestamps.
 *
 * Picked status timestamp is reduced by 1 to prevent it from winning over
 * a later status that was set at the exact same millisecond (e.g. when
 * interest is asked at the same time as picking).
 */
export const buildNewestStatus = (
  candidate: PickedCandidate,
): CandidateStatus => {
  const statuses = Object.keys(candidate).filter((key) =>
    CANDIDATE_STATUS_SORT_ORDER.includes(key as CandidateStatus),
  ) as CandidateStatus[];

  return statuses.reduce((prev, curr) => {
    const currAt =
      curr === CandidateStatus.Picked
        ? candidate[curr]!.at - 1
        : (candidate[curr]?.at ?? 0);
    const prevAt =
      prev === CandidateStatus.Picked
        ? candidate[prev]!.at - 1
        : (candidate[prev]?.at ?? 0);
    return currAt > prevAt ? curr : prev;
  });
};

/**
 * Secondary sort rank for candidates in the interest area (newestStatus === Interested).
 * Differentiates between: interested (success=true), not interested (success=false).
 * InterestAsked-only candidates fall after Interested naturally via statusRank.
 *
 * Returns 0 for candidates outside the Interested group (no effect on sort).
 */
export const interestSubRank = (
  c: PickedCandidateWithNewestStatus,
): number => {
  if (c.newestStatus !== CandidateStatus.Interested) return 0;
  if (c.interested?.success === true) return 1;
  if (c.interested?.success === false) return 2;
  return 0;
};

/**
 * Sort rank for the rejected group based on interest history.
 * All rejected candidates share newestStatus === EezyRejected, so interest
 * history must be checked directly from fields rather than newestStatus.
 *
 * Order: kysytty (no response) → kiinnostunut → ei kiinnostunut → no history
 */
export const rejectedInterestSubRank = (
  c: PickedCandidateWithNewestStatus,
): number => {
  if (c.interestAsked && !c.interested) return 1;
  if (c.interested?.success === true) return 2;
  if (c.interested?.success === false) return 3;
  return 4;
};

/**
 * Sorts picked (non-rejected) candidates.
 *
 * Primary order (via CANDIDATE_STATUS_SORT_ORDER + newestStatus):
 *   WorkAgreementAccepted → Offered → CustomerApproved → Introduced
 *   → Interested (success=true) → Interested (success=false) → InterestAsked
 *   → Picked
 *
 * Within Picked group, secondary order is talent status:
 *   ACTIVE (joutsen) → NEW (tipu) → LATER (alligaattori)
 *
 * Tie-breakers: lastName → firstName
 */
export const sortPickedCandidates = (
  candidates: PickedCandidate[],
): PickedCandidateWithNewestStatus[] => {
  const withNewestStatus: PickedCandidateWithNewestStatus[] = candidates.map(
    (candidate) => ({
      ...candidate,
      newestStatus: buildNewestStatus(candidate),
    }),
  );

  return withNewestStatus.sort(
    (a, b) =>
      CANDIDATE_STATUS_SORT_ORDER.indexOf(a.newestStatus) -
        CANDIDATE_STATUS_SORT_ORDER.indexOf(b.newestStatus) ||
      interestSubRank(a) - interestSubRank(b) ||
      TALENT_STATUS_SORT_ORDER.indexOf(a.status) -
        TALENT_STATUS_SORT_ORDER.indexOf(b.status) ||
      a.lastName.localeCompare(b.lastName) ||
      a.firstName.localeCompare(b.firstName),
  );
};

/**
 * Sorts rejected (eezyRejected) candidates.
 *
 * Since all rejected candidates share newestStatus === EezyRejected,
 * interest history is checked directly from fields.
 *
 * Order: kysytty (no response) → kiinnostunut → ei kiinnostunut → no history
 *
 * Tie-breakers: talent status (ACTIVE → NEW → LATER) → lastName → firstName
 */
export const sortRejectedCandidates = (
  candidates: PickedCandidateWithNewestStatus[],
): PickedCandidateWithNewestStatus[] =>
  [...candidates].sort(
    (a, b) =>
      rejectedInterestSubRank(a) - rejectedInterestSubRank(b) ||
      TALENT_STATUS_SORT_ORDER.indexOf(a.status) -
        TALENT_STATUS_SORT_ORDER.indexOf(b.status) ||
      a.lastName.localeCompare(b.lastName) ||
      a.firstName.localeCompare(b.firstName),
  );
